'use client'

import { createContext, useContext, useMemo, type ReactNode } from 'react'

import {
  useFlag,
  detectBot,
  type BotDetectionResult,
} from '@b-stock/fe-core/bstock-next/helpers'

import { FeatureFlags } from '../featureFlags'

type BotDetectionContextValue = Omit<BotDetectionResult, 'reason'> & {
  userAgent: string | null
}

const BotDetectionContext = createContext<BotDetectionContextValue>({
  excludeFromAnalytics: false,
  userAgent: null,
})

/**
 * Hook to access bot detection state
 *
 * @returns Object containing:
 * - excludeFromAnalytics: Whether this user agent should be excluded from analytics
 * - reason: Why it's excluded (if applicable)
 * - userAgent: The detected user agent string
 */
export function useBotDetection(): BotDetectionContextValue {
  return useContext(BotDetectionContext)
}

type BotDetectionProviderProps = {
  children: ReactNode
  /**
   * User agent string from server-side headers
   * Falls back to navigator.userAgent on client
   */
  serverUserAgent?: string
}

/**
 * Provider that determines if the current user should be blocked from analytics tracking
 *
 * Wraps children with bot detection context that can be consumed via useBotDetection()
 * Configuration comes from LaunchDarkly flag 'configure-telemetry-bot-filter'
 */
export function BotDetectionProvider({
  children,
  serverUserAgent,
}: BotDetectionProviderProps) {
  // Get config from LaunchDarkly flag 'configure-telemetry-bot-filter'
  const config = useFlag(FeatureFlags.CONFIGURE_TELEMETRY_BOT_FILTER)

  const value = useMemo<BotDetectionContextValue>(() => {
    // Prefer client-side user agent when available
    const userAgent =
      typeof window !== 'undefined'
        ? navigator.userAgent
        : (serverUserAgent ?? null)

    const detection = detectBot(userAgent, config)

    return {
      excludeFromAnalytics: detection.excludeFromAnalytics,
      userAgent,
    }
  }, [serverUserAgent, config])

  return (
    <BotDetectionContext.Provider value={value}>
      {children}
    </BotDetectionContext.Provider>
  )
}
